import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';

import {
  downloadSalesOrderExcelWithSelection,
  fetchSalesOrderExcelOptions,
} from '#/api/crmMgmt/salesOrder';
import { cn } from '#/lib/utils';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { getSalesOrderApiErrorMessage } from '#/modules/crmMgmt/salesOrder/helper/apiError';
import { getFileTransferErrorMessage } from '#/utils/blobResponseError';
import { triggerBlobDownload } from '#/utils/downloadBlob';
import { toast } from '#/utils/toast';

function buildDefaultSelection(lines) {
  const next = {};
  for (const line of lines ?? []) {
    const lineId = line.salesOrderLineId;
    if (!lineId) {
      continue;
    }
    next[lineId] = {
      mtrs: Object.fromEntries(
        (line.mtrs ?? [])
          .map((mtr) => mtr.customerOrderMtrSnapshotId)
          .filter(Boolean)
          .map((id) => [id, true]),
      ),
      extras: Object.fromEntries(
        (line.extraItems ?? [])
          .map((item) => item.id)
          .filter(Boolean)
          .map((id) => [id, true]),
      ),
    };
  }
  return next;
}

function formatProductLabel(product) {
  const name = product?.name || '—';
  const code = product?.productNo ?? product?.code;
  return code ? `${name}(${code})` : name;
}

function formatQtyUnit(qty, unitName) {
  const qtyText = qty == null || qty === '' ? '—' : String(qty);
  return unitName ? `${qtyText} ${unitName}` : qtyText;
}

/**
 * @param {Record<string, boolean>} checkedById
 * @param {string[]} ids
 * @returns {boolean | 'indeterminate'}
 */
function getSectionCheckState(checkedById, ids) {
  if (ids.length === 0) {
    return false;
  }
  let checkedCount = 0;
  for (const id of ids) {
    if (checkedById?.[id]) {
      checkedCount += 1;
    }
  }
  if (checkedCount === 0) {
    return false;
  }
  if (checkedCount === ids.length) {
    return true;
  }
  return 'indeterminate';
}

/**
 * 開啟時掛載：載入 excel-options、預設全選、送 POST 下載。
 */
const ExcelPrintDialogSession = ({
  open,
  onOpenChange,
  salesOrderId,
  onPendingChange,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lines, setLines] = useState([]);
  const [selection, setSelection] = useState({});
  const [expandedLineIds, setExpandedLineIds] = useState(() => new Set());
  const [hideAmounts, setHideAmounts] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    onPendingChange?.(isDownloading);
    return () => {
      onPendingChange?.(false);
    };
  }, [isDownloading, onPendingChange]);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      if (!salesOrderId) {
        setIsLoading(false);
        setLoadError('缺少銷貨單 id');
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      try {
        const result = await fetchSalesOrderExcelOptions(salesOrderId);
        if (cancelled) {
          return;
        }
        const nextLines = result?.data?.lines ?? [];
        const firstLineId = nextLines[0]?.salesOrderLineId;
        setLines(nextLines);
        setSelection(buildDefaultSelection(nextLines));
        setExpandedLineIds(firstLineId ? new Set([firstLineId]) : new Set());
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message = getSalesOrderApiErrorMessage(error, '讀取列印選項失敗');
        setLoadError(message);
        toast.error(message);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [salesOrderId]);

  function handleCancel() {
    if (isDownloading) {
      return;
    }
    onOpenChange(false);
  }

  function toggleExpanded(lineId) {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  }

  function toggleMtr(lineId, snapshotId, checked) {
    setSelection((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        mtrs: {
          ...(prev[lineId]?.mtrs ?? {}),
          [snapshotId]: checked,
        },
        extras: prev[lineId]?.extras ?? {},
      },
    }));
  }

  function toggleExtra(lineId, extraId, checked) {
    setSelection((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        mtrs: prev[lineId]?.mtrs ?? {},
        extras: {
          ...(prev[lineId]?.extras ?? {}),
          [extraId]: checked,
        },
      },
    }));
  }

  function setAllMtrs(lineId, ids, checked) {
    setSelection((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        mtrs: Object.fromEntries(ids.map((id) => [id, checked])),
        extras: prev[lineId]?.extras ?? {},
      },
    }));
  }

  function setAllExtras(lineId, ids, checked) {
    setSelection((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        mtrs: prev[lineId]?.mtrs ?? {},
        extras: Object.fromEntries(ids.map((id) => [id, checked])),
      },
    }));
  }

  async function handleConfirm() {
    if (!salesOrderId || isDownloading || isLoading || loadError) {
      return;
    }

    const lineOptions = lines
      .map((line) => {
        const lineId = line.salesOrderLineId;
        if (!lineId) {
          return null;
        }
        const lineSel = selection[lineId] ?? { mtrs: {}, extras: {} };
        return {
          salesOrderLineId: lineId,
          printMtrSnapshotIds: Object.entries(lineSel.mtrs ?? {})
            .filter(([, checked]) => checked)
            .map(([id]) => id),
          printExtraItemIds: Object.entries(lineSel.extras ?? {})
            .filter(([, checked]) => checked)
            .map(([id]) => id),
        };
      })
      .filter(Boolean);

    setIsDownloading(true);
    try {
      const { blob, filename } = await downloadSalesOrderExcelWithSelection(
        salesOrderId,
        { hideAmounts, lineOptions },
      );
      triggerBlobDownload(blob, filename);
      onOpenChange(false);
      toast.success('已下載 Excel');
    } catch (error) {
      toast.error(
        getFileTransferErrorMessage(error)
        || getSalesOrderApiErrorMessage(error, '下載 Excel 失敗'),
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isDownloading) {
          return;
        }
        onOpenChange(nextOpen);
      }}
      title="選列列印"
      description="依產品勾選要列印的物料與附加項目；產品主列一律列印。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-2xl"
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={isDownloading ? '下載中…' : '下載 Excel'}
      confirmDisabled={isLoading || Boolean(loadError) || isDownloading || !salesOrderId}
      cancelText="取消"
    >
      <div className="mb-3 flex items-center gap-2">
        <Checkbox
          id="sales-order-excel-hide-amounts"
          checked={hideAmounts}
          disabled={isDownloading}
          onCheckedChange={(value) => setHideAmounts(value === true)}
        />
        <label
          htmlFor="sales-order-excel-hide-amounts"
          className="cursor-pointer text-sm text-slate-700"
        >
          隱藏金額
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">載入列印選項中…</p>
      ) : null}

      {!isLoading && loadError ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : null}

      {!isLoading && !loadError && lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">此銷貨單沒有可列印的產品列。</p>
      ) : null}

      {!isLoading && !loadError && lines.length > 0 ? (
        <ul className="m-0 list-none space-y-3 p-0">
          {lines.map((line) => {
            const lineId = line.salesOrderLineId;
            const isOpen = expandedLineIds.has(lineId);
            const lineSel = selection[lineId] ?? { mtrs: {}, extras: {} };
            const mtrs = line.mtrs ?? [];
            const extras = line.extraItems ?? [];
            const mtrIds = mtrs
              .map((mtr) => mtr.customerOrderMtrSnapshotId)
              .filter(Boolean);
            const extraIds = extras.map((item) => item.id).filter(Boolean);
            const mtrCheckState = getSectionCheckState(lineSel.mtrs, mtrIds);
            const extraCheckState = getSectionCheckState(lineSel.extras, extraIds);

            return (
              <li
                key={lineId}
                className={cn(
                  'overflow-hidden rounded-md border border-slate-200',
                  isOpen && 'bg-slate-50/40',
                )}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left',
                    'transition-colors hover:text-primary-600',
                    isOpen && 'border-b border-slate-200 bg-slate-50',
                  )}
                  aria-expanded={isOpen}
                  onClick={() => toggleExpanded(lineId)}
                >
                  <ChevronDown
                    size={18}
                    className={cn(
                      'shortcut_transition-transform shrink-0 origin-center text-gray-600',
                      isOpen ? 'rotate-0' : '-rotate-90',
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">
                    {formatProductLabel(line.product)}
                  </span>
                </button>

                <div
                  className={cn(
                    'grid shortcut_transition-disclosure',
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                  aria-hidden={!isOpen}
                >
                  <div className="min-h-0 overflow-hidden">
                    <div className="space-y-3 px-3 py-3">
                      {mtrs.length === 0 && extras.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          無可勾選的物料或附加項目（仍會列印產品主列）。
                        </p>
                      ) : null}

                      {mtrs.length > 0 ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={mtrCheckState}
                              disabled={isDownloading || mtrIds.length === 0}
                              aria-label="全選物料"
                              onCheckedChange={(value) => {
                                setAllMtrs(lineId, mtrIds, value === true);
                              }}
                            />
                            <span className="text-xs font-medium text-slate-500">
                              物料
                            </span>
                          </div>
                          <ul className="m-0 list-none space-y-1 p-0">
                            {mtrs.map((mtr) => {
                              const snapshotId = mtr.customerOrderMtrSnapshotId;
                              const checked = Boolean(lineSel.mtrs?.[snapshotId]);
                              const label = mtr.mtrNo
                                ? `${mtr.mtrName || '—'}(${mtr.mtrNo})`
                                : (mtr.mtrName || '—');

                              return (
                                <li key={snapshotId}>
                                  <label
                                    className={cn(
                                      'flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5',
                                      'hover:bg-slate-50',
                                      checked && 'bg-primary-50',
                                    )}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      disabled={isDownloading || !snapshotId}
                                      onCheckedChange={(value) => {
                                        toggleMtr(
                                          lineId,
                                          snapshotId,
                                          value === true,
                                        );
                                      }}
                                      className="mt-0.5"
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-sm text-slate-800">
                                        {label}
                                      </span>
                                      <span className="block text-xs text-slate-500">
                                        {formatQtyUnit(mtr.qty, mtr.unitName)}
                                        {mtr.remark ? ` · ${mtr.remark}` : ''}
                                      </span>
                                    </span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null}

                      {extras.length > 0 ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={extraCheckState}
                              disabled={isDownloading || extraIds.length === 0}
                              aria-label="全選附加項目"
                              onCheckedChange={(value) => {
                                setAllExtras(lineId, extraIds, value === true);
                              }}
                            />
                            <span className="text-xs font-medium text-slate-500">
                              附加項目
                            </span>
                          </div>
                          <ul className="m-0 list-none space-y-1 p-0">
                            {extras.map((item) => {
                              const extraId = item.id;
                              const checked = Boolean(lineSel.extras?.[extraId]);

                              return (
                                <li key={extraId}>
                                  <label
                                    className={cn(
                                      'flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5',
                                      'hover:bg-slate-50',
                                      checked && 'bg-primary-50',
                                    )}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      disabled={isDownloading || !extraId}
                                      onCheckedChange={(value) => {
                                        toggleExtra(
                                          lineId,
                                          extraId,
                                          value === true,
                                        );
                                      }}
                                      className="mt-0.5"
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-sm text-slate-800">
                                        {item.itemName || '—'}
                                      </span>
                                      <span className="block text-xs text-slate-500">
                                        {formatQtyUnit(
                                          item.actualQty,
                                          item.unitName,
                                        )}
                                        {item.remark ? ` · ${item.remark}` : ''}
                                      </span>
                                    </span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </AppDialog>
  );
};

ExcelPrintDialogSession.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  salesOrderId: PropTypes.string,
  onPendingChange: PropTypes.func,
};

const SalesOrderExcelPrintDialog = ({
  open,
  onOpenChange,
  salesOrderId,
  onPendingChange,
}) => {
  if (!open) {
    return (
      <AppDialog
        open={false}
        onOpenChange={onOpenChange}
        title="選列列印"
        description="依產品勾選要列印的物料與附加項目；產品主列一律列印。"
        headerVariant="muted"
      />
    );
  }

  return (
    <ExcelPrintDialogSession
      open={open}
      onOpenChange={onOpenChange}
      salesOrderId={salesOrderId}
      onPendingChange={onPendingChange}
    />
  );
};

SalesOrderExcelPrintDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  salesOrderId: PropTypes.string,
  onPendingChange: PropTypes.func,
};

export default SalesOrderExcelPrintDialog;
